import { Injectable } from '@angular/core';

/** Describes the account currently being dragged from a customer table. */
export interface DraggedAccountPayload {
  accountId: string;
  accountName: string;
}

/**
 * Shared state for the "drag an account row onto the calendar" gesture.
 *
 * Mirrors ContactDragStateService — the rationale (Android System WebView
 * strips custom dataTransfer MIME types; iOS WKWebView stalls on custom UTIs)
 * applies identically to account drags, so the payload travels through app
 * state, not through dataTransfer. The payload auto-clears on `dragend`,
 * which always fires on the source element after the drop, so the drop
 * handler still reads it in time.
 */
@Injectable({ providedIn: 'root' })
export class AccountDragStateService {
  private payload: DraggedAccountPayload | null = null;
  private readonly clearOnDragEnd = (): void => this.endDrag();

  /** The account currently being dragged, or null when no drag is in progress. */
  get draggedAccount(): DraggedAccountPayload | null {
    return this.payload;
  }

  /** Publish the dragged account and arm an auto-clear when the drag ends. */
  beginDrag(payload: DraggedAccountPayload): void {
    this.payload = payload;
    // Re-register a single capture-phase listener so a fresh drag never stacks
    // duplicates or leaks one from a previous drag.
    document.removeEventListener('dragend', this.clearOnDragEnd, true);
    document.addEventListener('dragend', this.clearOnDragEnd, true);
  }

  /** Clear the dragged account. Normally invoked automatically on `dragend`. */
  endDrag(): void {
    this.payload = null;
    document.removeEventListener('dragend', this.clearOnDragEnd, true);
  }
}
